<template>
  <mdb-modal :show="show" @close="close()" size="lg">
    <mdb-modal-header class="aqua-gradient">
      <mdb-modal-title>Set Data to Update</mdb-modal-title>
    </mdb-modal-header>
    <mdb-modal-body class="overflow-hidden d-flex">
      <mdb-container>
        <mdb-row class="h-100">
          <mdb-col col="4" class="h-100">
            <bridge8-select-list
              class="ml-2 mr-2 h-100"
              entity="label"
              :options="labels"
              label="Node Label"
              :search="true"
              @selected="label = $event"
            />
          </mdb-col>
          <mdb-col col="8" class="h-100 table-wrapper">
            <div class="d-flex flex-row justify-content-around align-content-center">
              <div
                class="drag-and-drop-zone"
                @drop.prevent="onDrop($event)"
                @dragover.prevent
              >
                Drag and drop XLSX file here
              </div>
              <div>
                <mdb-btn
                  rounded
                  size="sm"
                  gradient="aqua"
                  icon="clipboard"
                  title="Paste table data from clipboard"
                  @click="fromClipboard()"
                />
                <mdb-btn-group>
                  <mdb-btn
                    rounded
                    icon="plus"
                    gradient="aqua"
                    size="sm"
                    class="mb-2"
                    title="Add data column"
                    @click="fields.push(null)"
                  />
                  <mdb-btn
                    :disabled="fields.length === 0"
                    rounded
                    icon="minus"
                    gradient="sunny-morning"
                    size="sm"
                    class="mb-2"
                    title="Remove last data column"
                    @click="fields.pop()"
                  />
                </mdb-btn-group>
              </div>
            </div>
            <mdb-tbl class="mt-3">
              <mdb-tbl-head>
                <tr>
                  <th
                    v-for="(field, index) in fields"
                    :key="`header-cell-${index}`"
                    class="cell header-cell pt-0 pb-0"
                    :class="{ 'ml-1': index > 0, 'mr-1': index < fields.length}"
                  >
                    <bridge8-autocomplete
                      size="sm"
                      class="prop-autocomplete m-0 text-small"
                      :search-string="fields[index]"
                      v-model="fields[index]"
                      :data="props"
                      :min-chars-to-search="0"
                      @change="fields[index] = $event"
                    />
                  </th>
                </tr>
              </mdb-tbl-head>
              <mdb-tbl-body>
                <tr
                  v-for="(line, i) in table"
                  :key="`table-line-${i}`"
                >
                  <td
                    v-for="(val, j) in line"
                    :key="`table-cell-${i}-${j}`"
                    class="cell body-cell text-left"
                  >{{ val }}</td>
                </tr>
              </mdb-tbl-body>
            </mdb-tbl>
          </mdb-col>
        </mdb-row>
      </mdb-container>
    </mdb-modal-body>
    <mdb-modal-footer>
      <mdb-btn
        :disabled="!label || fields.length < 2 || table.length === 0"
        rounded
        size="sm"
        gradient="aqua"
        @click.native="close(true)"
        title="Submit data to update"
      >Update</mdb-btn>
    </mdb-modal-footer>
  </mdb-modal>
</template>

<script>
import {errorCatch} from '../utils'
import {mapActions} from 'vuex'
import {
  mdbContainer,
  mdbModal,
  mdbModalHeader,
  mdbModalTitle,
  mdbModalBody,
  mdbModalFooter,
  mdbBtnGroup,
  mdbBtn,
  mdbTbl,
  mdbTblHead,
  mdbTblBody,
  mdbRow,
  mdbCol
} from 'mdbvue'
import Bridge8SelectList from './Bridge8SelectList'
import Bridge8Autocomplete from './Bridge8Autocomplete'
import XLSX from 'xlsx'

// TODO: Common modal omponent
export default {
  name: 'AdminTableUpdateModal',
  data () {
    return {
      labels: [],
      label: null,
      fields: [],
      props: [],
      table: [],
      show: false,
      callback: data => { console.log('Data entered: ', data) }
    }
  },
  methods: {
    ...mapActions(['apiGetLabels', 'apiGetProps']),
    close (submit = false) {
      this.show = false
      if (submit) {
        const data = {
          label: this.label,
          fields: this.fields,
          table: this.table
        }
        this.table = []
        this.label = null
        this.fields = []
        this.callback(data)
      }
      this.callback = data => { console.log('Data entered: ', data) }
    },
    fromClipboard () {
      this.$bus.$emit('modal-textarea', text => {
        const data = text.split('\n').map(line => line.split('\t'))
        const valid = this.validateData(data)
        if (valid) {
          this.table = data
        }
      })
    },
    onDrop (e) {
      const files = e.dataTransfer.files
      if (files.length === 0) { return }
      const file = files[0]
      const reader = new FileReader()
      reader.onload = event => {
        const workbook = XLSX.read(event.target.result, {type: 'binary'})
        const worksheet = workbook.Sheets[workbook.SheetNames[0]]
        const range = XLSX.utils.decode_range(worksheet['!ref'])
        const data = []
        let r = 0
        for (let row = range.s.r; row <= range.e.r; ++row) {
          for (let col = range.s.c; col <= range.e.c; ++col) {
            const cellref = XLSX.utils.encode_cell({c: col, r: row})
            if (!worksheet[cellref]) continue
            const cell = worksheet[cellref]
            const val = cell.v
            if (!data[r]) {
              data[r] = []
            }
            data[r].push(val)
          }
          r++
        }

        if (this.validateData(data)) {
          this.table = data
        }
      }
      reader.readAsBinaryString(file)
    },
    validateData (data) {
      const BreakException = {}
      let valid = true
      try {
        if (this.fields.length < 2) {
          this.$notify.warn({
            title: `Too few columns`,
            message: `You have to make at least two columns. One is for node search property name. Second and further for node update property names`,
            position: 'top right',
            timeOut: 10000,
            closeButton: true
          })
          throw BreakException
        }
        data.forEach((line, lineIndex) => {
          if (line.length !== this.fields.length) {
            this.$notify.warn({
              title: `Error on line ${lineIndex + 1}`,
              message: `Please check columns number. Expected: ${this.fields.length}. Got: ${line.length}`,
              position: 'top right',
              timeOut: 10000,
              closeButton: true
            })
            throw BreakException
          }
        })
      } catch (e) {
        if (e !== BreakException) throw e
        valid = false
      }
      return valid
    }
  },
  mounted () {
    this.$bus.$on('admin-table-update-modal', callback => {
      this.callback = callback
      this.show = true
    })
    this.apiGetLabels().then(labels => { this.labels = labels })
      .catch(errorCatch('Get Labels failed in Table Update Modal', this))
  },
  beforeDestroy () {
    this.$bus.$off('admin-table-update-modal')
  },
  watch: {
    label (newVal, oldVal) {
      if (newVal === null) {
        this.props = []
        return
      }
      if (newVal !== oldVal) {
        this.apiGetProps(newVal).then(props => { this.props = props })
          .catch(errorCatch('Get Props failed in Table Update Modal', this))
      }
    }
  },
  components: {
    mdbContainer,
    mdbModal,
    mdbModalHeader,
    mdbModalTitle,
    mdbModalBody,
    mdbModalFooter,
    mdbBtnGroup,
    mdbBtn,
    mdbTbl,
    mdbTblHead,
    mdbTblBody,
    mdbRow,
    mdbCol,
    Bridge8SelectList,
    Bridge8Autocomplete
  }
}
</script>

<style lang="sass" scoped>
.drag-and-drop-zone
  border: darkcyan dotted 1px
  padding: 1rem 0.5rem

.table-wrapper
  overflow: auto

.cell
  border-left: 1px solid rgb(222, 226, 230)

  &:first-child
    border-left: none

  &.header-cell
    input.form-control.mdb-autocomplete
        font-weight: bold!important
</style>



// WEBPACK FOOTER //
// src/components/AdminTableUpdateModal.vue